/* -------------------------------------------------- */
/*  src/Ontology/ontology.js                          */
/*  仅显示最外层行星环（第 5 环），无行星，仅标签       */
/* -------------------------------------------------- */
import React, { useRef, useState, useMemo, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Stars, Environment } from "@react-three/drei";
import * as THREE from "three";
import Angel4 from "./angel4";
import "./ontology.css";

/* 五环标签原始数据 —— 仍然保留，以便以后想恢复 */
const DATA = [
  [""], // 1
  [""], // 2
  [""], // 3
  [""], // 4
  [
    "Shopping", "Food", "Housing", "Transportation", "Travel",
    "Social", "Dating", "Emotion", "Search", "Work",
    "Project", "Finance", "Legal", "Language", "Creativity",
    "Healthcare", "Education", "Fitness", "Sleep",
  ], // 5 ← 只渲染这一环
];

const OUTER_IDX  = DATA.length - 1;        // 4
const OUTER_RING = OUTER_IDX + 1;          // 5
const LABELS     = DATA[OUTER_IDX];

/* ─────────────────────────────── */
/*  场景 + 标签投射（无行星）       */
/* ─────────────────────────────── */
function SceneAndLabels({ onUpdate }) {
  /* 行星参数只用于计算轨迹，不真正渲染球体 */
  const planets = useMemo(() => {
    const radius = 2 + OUTER_RING * 1.8;          // 与原公式一致
    const height = OUTER_IDX * 0.2 + 0.5;
    const step   = (2 * Math.PI) / LABELS.length;
    return LABELS.map((label, i) => ({
      id:    `outer-${i}`,
      label,
      radius,
      height,
      angle0: i * step,
      speed:  0.05 / OUTER_RING,
    }));
  }, []);

  /* 帧循环：计算每个标签的屏幕投影 + 简易避碰 */
  useFrame(({ camera, size, clock }) => {
    const positions = planets.map((p) => {
      const t = clock.getElapsedTime() * p.speed;
      const a = p.angle0 + t;

      /* 世界坐标 */
      const world = new THREE.Vector3(
        p.radius * Math.cos(a),
        p.height,
        p.radius * Math.sin(a)
      );

      /* 投影到屏幕 */
      world.project(camera);
      return {
        id: p.id,
        label: p.label,
        x: (world.x * 0.5 + 0.5) * size.width,
        y: (-world.y * 0.5 + 0.5) * size.height,
      };
    });

    /* 简单避碰（与原逻辑相同） */
    const minDist = 60;
    for (let pass = 0; pass < 3; pass++) {
      for (let i = 0; i < positions.length; i++) {
        for (let j = i + 1; j < positions.length; j++) {
          const A = positions[i], B = positions[j];
          const dx = B.x - A.x,   dy = B.y - A.y;
          const d  = Math.hypot(dx, dy) || 0.001;
          if (d < minDist) {
            const overlap = (minDist - d) / 2;
            const ux = dx / d, uy = dy / d;
            A.x -= ux * overlap;  A.y -= uy * overlap;
            B.x += ux * overlap;  B.y += uy * overlap;
          }
        }
      }
    }

    onUpdate(positions);
  });

  return (
    <>
      {/* 环境与星空 */}
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 5, 5]} intensity={1} />
      <Environment preset="sunset" background={false} />
      <Stars radius={100} depth={50} count={2000} factor={4} fade />

      {/* 外环轨道（薄薄一圈） */}
      <mesh
        position={[0, OUTER_IDX * 0.2 + 0.5, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <torusGeometry args={[2 + OUTER_RING * 1.8, 0.02, 16, 128]} />
        <meshBasicMaterial color="#88aaff" transparent opacity={0.25} />
      </mesh>
    </>
  );
}

/* ─────────────────────────────── */
/*  顶层组件：背景 + Canvas + 标签层 */
/* ─────────────────────────────── */
export default function Ontology3D() {
  const [labels, setLabels] = useState([]);
  const [disableDrag, setDisableDrag] = useState(false);
  const controlsRef = useRef(null);
  // 🔧 每次 disableDrag 变化时，手动启用/禁用 OrbitControls


  /* 监听 angel4 的 bg-interactive 事件 */
  useEffect(() => {
    const handler = (e) => setDisableDrag(!e.detail);
    window.addEventListener("bg-interactive", handler);
    return () => window.removeEventListener("bg-interactive", handler);
  }, []);








  return (
    <>
      {/* 背景（Angel4） */}
      <Angel4 />

      {/* Three.js Canvas */}
      <Canvas
        style={{ position: "absolute", inset: 0, pointerEvents: disableDrag ? "none" : "auto" }}
        camera={{ position: [0, 6, 12], fov: 55 }}
      >
        <OrbitControls ref={controlsRef} makeDefault enableDamping   enabled={!disableDrag} />
      
        <SceneAndLabels onUpdate={setLabels} />
      </Canvas>

      {/* HTML 标签层 */}
      <div className="labels-overlay">
        {labels.map(({ id, label, x, y }) => (
          <div key={id} className="label" style={{ left: x, top: y }}>
            {label}
          </div>
        ))}
      </div>
    </>
  );
}
